import * as React from 'react';
import { ErrorNotification, SuccessNotification } from '@verclam/toast-v2';

/**
 * `eventName` matches the `type` of the actions dispatched by `Application/helpers/**`. Every
 * interview/recording/consent creation happens off-screen, from the transfer handler's own
 * composite call: a toast there would be noise (the failed-transfer state already renders on the
 * queue item itself), so only the consent-related listeners remain.
 *
 * The transcript review block (V-24) adds eleven listeners of its own. Two are deliberately absent:
 * a turn revision that succeeds updates its row under the user's own eyes, so a toast on every
 * validated keystroke would be noise, and `TRANSCRIPT_EXPORTED` needs none either — the browser's own
 * download already announces it.
 *
 * A failed rename has no toast: the error shows under the title field.
 */
const InterviewsNotifications: React.FC = () => {
	return (
		<React.Fragment>
			<SuccessNotification
				eventName="INTERVIEW_CONSENT_WITHDRAWN"
				title="Consentement retiré"
				message="Le consentement a été retiré et l'enregistrement supprimé."
			/>
			<ErrorNotification
				eventName="INTERVIEW_CONSENT_WITHDRAWAL_FAILED"
				title="Erreur"
				message="Le consentement n'a pas pu être retiré."
			/>
			<ErrorNotification
				eventName="INTERVIEW_CONSENT_FETCH_FAILED"
				title="Erreur"
				message="Le consentement n'a pas pu être chargé."
			/>
			<SuccessNotification
				eventName="INTERVIEW_RENAMED"
				title="Nom modifié"
				message="Le nouveau nom a été enregistré."
			/>
			<SuccessNotification
				eventName="INTERVIEW_DELETED"
				title="Suppression effectuée"
				message="La suppression a été effectuée."
			/>
			<ErrorNotification
				eventName="INTERVIEW_DELETION_FAILED"
				title="Erreur"
				message="La suppression a échoué."
			/>
			<ErrorNotification
				eventName="INTERVIEW_FETCH_FAILED"
				title="Erreur"
				message="Le rechargement a échoué."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_TURN_REVISION_FAILED"
				title="Erreur"
				message="La correction n'a pas pu être enregistrée."
			/>
			<SuccessNotification
				eventName="TRANSCRIPT_SPEAKER_ASSIGNED"
				title="Locuteur attribué"
				message="L'attribution a été enregistrée."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_SPEAKER_ASSIGNMENT_FAILED"
				title="Erreur"
				message="L'attribution n'a pas pu être enregistrée."
			/>
			<SuccessNotification
				eventName="TRANSCRIPT_SPEAKER_UNASSIGNED"
				title="Attribution retirée"
				message="Ce locuteur redevient non attribué."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_SPEAKER_UNASSIGNMENT_FAILED"
				title="Erreur"
				message="L'attribution n'a pas pu être retirée."
			/>
			<SuccessNotification
				eventName="TRANSCRIPT_SPEAKERS_MERGED"
				title="Locuteurs fusionnés"
				message="Les tours ont été regroupés."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_SPEAKER_MERGE_FAILED"
				title="Erreur"
				message="La fusion n'a pas pu être effectuée."
			/>
			<SuccessNotification
				eventName="TRANSCRIPT_REVIEWED"
				title="Transcript relu"
				message="La relecture a été enregistrée."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_REVIEW_FAILED"
				title="Erreur"
				message="La relecture n'a pas pu être enregistrée."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_EXPORT_FAILED"
				title="Erreur"
				message="L'export n'a pas pu être produit."
			/>
			<ErrorNotification
				eventName="TRANSCRIPT_TURNS_FETCH_FAILED"
				title="Erreur"
				message="La suite du transcript n'a pas pu être chargée."
			/>
		</React.Fragment>
	);
};

export default InterviewsNotifications;
